import React from "react";
import {
  Container,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  alpha,
  Stack
} from "@mui/material";
import {
  PhoneAndroid as PhoneIcon,
  Analytics as AnalyticsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTenantConfig } from "../hooks/useTenant";

const P = {
  main: '#8b5cf6',
  light: '#a78bfa',
  dark: '#7c3aed',
};

const features = [
  {
    title: "Aidat Hatırlatma Bildirimleri",
    text: "Aidat ödeme zamanı geldiğinde üyelerinize otomatik bildirim gönderin.",
    images: '/assets/icon/transactions.webp',
  },
  {
    title: "Geciken Aidat Uyarıları",
    text: "Gecikmiş ödemeler için otomatik hatırlatma bildirimleri oluşturun.",
    images: '/assets/icon/clock.webp',
  },
  {
    title: "Ödeme Alındı Bildirimi",
    text: "Aidat ödemesi gerçekleştiğinde üyeye anında bilgilendirme gönderilir.",
    images: '/assets/icon/verified.webp',
  },
  {
    title: "Etkinlik ve Organizasyon Bildirimleri",
    text: "Dernek etkinlikleri ve organizasyonları hakkında üyelerinizi anında bilgilendirin.",
    images: '/assets/icon/event.webp',
  },
  {
    title: "E-İmza ve Yönetim Bildirimleri",
    text: "E-imza bekleyen belgeler ve yönetim süreçleri için yöneticilere bildirim gönderilir.",
    images: '/assets/icon/digital-signature.webp',
  },
  {
    title: "Doğum Günü ve Özel Gün Mesajları",
    text: "Üyelerinize doğum günü ve özel gün kutlama mesajlarını otomatik gönderin.",
    images: '/assets/icon/firework.webp',
  }
];

const Feature = ({ text }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: P.main,
        mt: 0.9,
        flexShrink: 0,
      }}
    />
    <Typography sx={{ color: '#64748b', fontSize: '0.95rem', lineHeight: 1.6 }}>
      {text}
    </Typography>
  </Box>
);

export default function PushNotification() {
  const { tenantName } = useTenantConfig();
  const navigate = useNavigate();

  return (
    <Box sx={{ overflow: 'hidden' }}>

      {/* ================= HERO ================= */}
      <Box
        sx={{
          pt: { xs: 6, md: 10 },
          pb: { xs: 6, md: 10 },
          background: `linear-gradient(135deg, ${alpha(P.main, 0.06)} 0%, #ffffff 100%)`,
          position: 'relative',
          overflow: 'hidden',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '-20%',
            right: '-10%',
            width: 600,
            height: 600,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(P.main, 0.08)} 0%, transparent 70%)`,
            filter: 'blur(80px)',
          }}
        />

        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 720, position: 'relative', zIndex: 1 }}>
            <Chip
              label="Mobil Bildirim Sistemi"
              sx={{
                mb: 3,
                fontWeight: 600,
                fontSize: '0.8rem',
                background: alpha(P.main, 0.1),
                color: P.dark,
                border: `1px solid ${alpha(P.main, 0.2)}`,
              }}
            />

            <Typography
              variant="h1"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                mb: 3,
                lineHeight: 1.1,
                color: '#0f172a',
                letterSpacing: '-0.02em',
              }}
            >
              Ücretsiz Toplu{' '}
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(135deg, ${P.main} 0%, ${P.light} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Bilgilendirme
              </Box>
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: '1rem', md: '1.15rem' },
                color: '#64748b',
                lineHeight: 1.7,
                mb: 4,
              }}
            >
              SMS maliyetlerinden kurtulun ve üyelerinize saniyeler içinde ulaşın.
              {tenantName} mobil uygulaması sayesinde tüm duyurularınızı push
              bildirimlerle anında iletebilir ve iletişim süreçlerinizi tamamen
              dijital hale getirebilirsiniz.
            </Typography>

            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/basvuru')}
              sx={{
                px: 5,
                py: 1.8,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${P.main} 0%, ${P.dark} 100%)`,
                boxShadow: `0 8px 24px ${alpha(P.main, 0.25)}`,
                transition: 'all 0.3s ease',
                textTransform: 'none',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 12px 32px ${alpha(P.main, 0.35)}`,
                },
              }}
            >
              Hemen Başla
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ================= MOBİL UYGULAMA AÇIKLAMA ================= */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderRadius: 50,
                  background: alpha(P.main, 0.08),
                  border: `1px solid ${alpha(P.main, 0.15)}`,
                  mb: 3,
                }}
              >
                <PhoneIcon sx={{ fontSize: '1rem', color: P.main }} />
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: P.main,
                    letterSpacing: '0.5px',
                  }}
                >
                  REACT NATIVE MOBİL UYGULAMA
                </Typography>
              </Box>

              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.8rem', md: '2.5rem' },
                  color: '#0f172a',
                  lineHeight: 1.3,
                  letterSpacing: '-0.01em',
                  mb: 3,
                }}
              >
                Derneğinize Özel Mobil Uygulama
              </Typography>

              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '1.05rem',
                  lineHeight: 1.8,
                  mb: 4,
                }}
              >
                React Native tabanlı mobil uygulama sayesinde üyelerinizle
                doğrudan iletişim kurabilir, tüm bilgilendirmeleri push
                bildirimlerle ücretsiz olarak iletebilirsiniz.
              </Typography>

              <Stack spacing={2}>
                <Feature text="Kurumsal kimliğinizi güçlendirirsiniz" />
                <Feature text="Üyelerinizle doğrudan iletişim kurarsınız" />
                <Feature text="SMS maliyetlerinden kurtulursunuz" />
                <Feature text="Duyurularınızı saniyeler içinde iletebilirsiniz" />
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 5,
                  borderRadius: 5,
                  background: `linear-gradient(145deg, ${alpha(P.main, 0.04)} 0%, ${alpha(P.light, 0.04)} 100%)`,
                  border: `1px solid ${alpha(P.main, 0.1)}`,
                  boxShadow: `0 20px 60px ${alpha(P.main, 0.08)}`,
                }}
              >
                <Stack spacing={3}>
                  {[
                    { image: '/assets/icon/mobiles-phones.webp', text: 'iOS & Android Destek' },
                    { image: '/assets/icon/notifications.webp', text: 'Anında Bildirim' },
                    { image: '/assets/icon/infinity.webp', text: 'Sınırsız Gönderim' },
                    { image: '/assets/icon/achievement.webp', text: 'Tamamen Ücretsiz' },
                  ].map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 1,
                        borderRadius: 3,
                        background: '#ffffff',
                        border: `1px solid ${alpha(P.main, 0.1)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateX(8px)',
                          boxShadow: `0 8px 24px ${alpha(P.main, 0.12)}`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 50,
                          height: 50,
                          borderRadius: 2,
                          background: `linear-gradient(135deg, #f65c5c00 0%, #8bbefa 100%)`,
                          color: 'white',
                        }}
                      >
                        <img
                          src={item.image}
                          alt={item.text}
                          style={{ width: 30, height: 30, objectFit: 'contain' }}
                        />
                      </Box>
                      <Typography sx={{ fontWeight: 600, color: '#0f172a' }}>
                        {item.text}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ================= BİLDİRİM ÖZELLİKLER ================= */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: `linear-gradient(180deg, #ffffff 0%, ${alpha(P.main, 0.02)} 100%)`,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.8rem', md: '2.5rem' },
                color: '#0f172a',
                lineHeight: 1.3,
                letterSpacing: '-0.01em',
                mb: 2,
              }}
            >
              Mobil Bildirim Özellikleri
            </Typography>
            <Typography
              sx={{
                color: '#64748b',
                fontSize: '1.05rem',
                maxWidth: 600,
                mx: 'auto',
              }}
            >
              Üyelerinizi her an bilgilendir, iletişimi güçlendir
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {features.map((item, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    border: `1px solid ${alpha(P.main, 0.1)}`,
                    boxShadow: 'none',
                    background: '#ffffff',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 16px 48px ${alpha(P.main, 0.12)}`,
                      borderColor: P.main,
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{
                      display: 'flex',
                      flexDirection:{ xs: 'column', md: 'row' },
                      gap: 2,
                      mb: 1
                    }}>
                      <Box
                        sx={{
                          width: 55,
                          height: 55,
                          borderRadius: 3,
                          background: `linear-gradient(135deg, ${alpha(P.main, 0.1)} 0%, ${alpha(P.light, 0.1)} 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <img
                          src={item.images}
                          alt={item.title}
                          style={{ width: 36, height: 36, objectFit: 'contain' }}
                        />
                      </Box>

                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          fontSize: '1.1rem',
                          color: '#0f172a',
                        }}
                      >
                        {item.title}
                      </Typography>
                    </Box>

                    <Typography
                      sx={{
                        color: '#64748b',
                        lineHeight: 1.7,
                        fontSize: '0.95rem',
                      }}
                    >
                      {item.text}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ================= ANALİTİK ================= */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 5,
                  borderRadius: 5,
                  background: `linear-gradient(145deg, ${alpha(P.main, 0.04)} 0%, ${alpha(P.light, 0.04)} 100%)`,
                  border: `1px solid ${alpha(P.main, 0.1)}`,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(P.main, 0.1)} 0%, transparent 70%)`,
                  }}
                />

                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <AnalyticsIcon
                    sx={{
                      fontSize: '3rem',
                      color: P.main,
                      mb: 3,
                    }}
                  />

                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#0f172a',
                      mb: 2,
                    }}
                  >
                    Veri ile İletişimi Yönetin
                  </Typography>

                  <Typography
                    sx={{
                      color: '#64748b',
                      lineHeight: 1.8,
                      fontSize: '1rem',
                    }}
                  >
                    Bildirim performanslarını analiz ederek üyelerinizle
                    kurduğunuz iletişimi sürekli geliştirebilirsiniz.
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderRadius: 50,
                  background: alpha(P.main, 0.08),
                  border: `1px solid ${alpha(P.main, 0.15)}`,
                  mb: 3,
                }}
              >
                <AnalyticsIcon sx={{ fontSize: '1rem', color: P.main }} />
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: P.main,
                    letterSpacing: '0.5px',
                  }}
                >
                  PERFORMANS TAKİBİ
                </Typography>
              </Box>

              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.8rem', md: '2.5rem' },
                  color: '#0f172a',
                  lineHeight: 1.3,
                  letterSpacing: '-0.01em',
                  mb: 3,
                }}
              >
                Bildirim Performansını Takip Edin
              </Typography>

              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '1.05rem',
                  lineHeight: 1.8,
                  mb: 4,
                }}
              >
                Yönetim paneli üzerinden gönderilen bildirimlerin etkisini
                analiz edebilir ve iletişim süreçlerinizi veriye dayalı
                şekilde yönetebilirsiniz.
              </Typography>

              <Stack spacing={2}>
                <Feature text="Kaç kişi bildirimi gördü" />
                <Feature text="Kaç kişi bildirime tıkladı" />
                <Feature text="Bildirim etkileşim oranı" />
                <Feature text="Duyuru performansı" />
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ================= CTA ================= */}
      <Box
        sx={{
          pb: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              background: `linear-gradient(135deg, ${P.main} 0%, ${P.dark} 100%)`,
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 4,
              py: { xs: 6, md: 8 },
              px: { xs: 3, md: 6 },
            }}
          >
            {/* Arka plan deseni */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.15,
                backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
                backgroundSize: '30px 30px'
              }}
            />

            {/* Decorative circles */}
            <Box
              sx={{
                position: 'absolute',
                top: '-10%',
                right: '-5%',
                width: 400,
                height: 400,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                filter: 'blur(60px)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: '-10%',
                left: '-5%',
                width: 300,
                height: 300,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
                filter: 'blur(50px)',
              }}
            />

            <Box sx={{ position: 'relative', textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '1.75rem', md: '2.5rem' },
                  fontWeight: 900,
                  mb: 3,
                  textShadow: '0 2px 10px rgba(0,0,0,0.1)'
                }}
              >
                Derneğiniz İçin Modern İletişim Altyapısı
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: '1.05rem', md: '1.2rem' },
                  mb: 5,
                  opacity: 0.95,
                  maxWidth: '620px',
                  mx: 'auto',
                  lineHeight: 1.7,
                  color: 'rgba(255,255,255,0.9)'
                }}
              >
                {tenantName} mobil uygulaması ile üyelerinizle güçlü ve hızlı
                bir iletişim kurun.
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={3}
                justifyContent="center"
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/basvuru')}
                  sx={{
                    bgcolor: 'white',
                    color:  P.main,
                    px: 5,
                    py: 2,
                    fontSize: '1.05rem',
                    fontWeight: 700,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    '&:hover': {
                      bgcolor: '#fafafa',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.2)'
                    }
                  }}
                >
                  Hemen Başvurun
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/basvuru')}
                  sx={{
                    px: 5,
                    py: 1.8,
                    fontSize: '1rem',
                    fontWeight: 600,
                    background: '#ffffff',
                    color: P.main,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    transition: 'all 0.3s ease',
                    textTransform: 'none',
                    '&:hover': {
                      background: '#ffffff',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
                    },
                  }}
                >
                  Mobil Uygulama Özelliklerini Keşfedin
                </Button>
              </Stack>
            </Box>
          </Box>
        </Container>
      </Box>

    </Box>
  );
}